import { Check } from "lucide-react";

/**
 * Reusable two-checkbox consent block used at BOTH gates:
 *  - Free-report submit (bottom of Step 5 in CoupleQuestionnaire)
 *  - Paid unlock button on /result/{id}
 *
 * Both checkboxes MUST be ticked before the parent's primary CTA is enabled;
 * the parent owns that gating via useState + a canSubmit boolean.
 *
 * Purpose (legal): capture explicit consent that
 *   1. the photos are the user's / partner's own and consent has been given, and
 *   2. AI-generated visuals are a reference / guideline only.
 * The parent persists these to the backend for the legal trail.
 */
export function ConsentBlock({
    ownership,
    setOwnership,
    aiDisclaimer,
    setAiDisclaimer,
    variant = "light",           // "light" (ivory bg) or "dark" (charcoal bg)
    idPrefix = "consent",
}) {
    const isDark = variant === "dark";
    const wrap = isDark
        ? "rounded-[18px] bg-ivory/5 p-4 md:p-5 ring-1 ring-ivory/10"
        : "rounded-[18px] bg-champagne/40 p-4 md:p-5 ring-1 ring-charcoal/10";
    return (
        <div className={`mt-5 space-y-3 ${wrap}`} data-testid={`${idPrefix}-block`}>
            <ConsentRow
                checked={ownership}
                onChange={setOwnership}
                variant={variant}
                testId={`${idPrefix}-ownership`}
                label={
                    <>
                        I confirm the uploaded photos are of me / my partner, they are genuine,
                        and I have my partner&apos;s consent to use them for this report.
                    </>
                }
            />
            <ConsentRow
                checked={aiDisclaimer}
                onChange={setAiDisclaimer}
                variant={variant}
                testId={`${idPrefix}-ai-disclaimer`}
                label={
                    <>
                        I understand the AI-generated images are for reference only, may not be
                        100&nbsp;% accurate, and I&apos;ll use them as a guideline to plan my
                        wedding — not a guarantee.
                    </>
                }
            />
        </div>
    );
}

function ConsentRow({ checked, onChange, label, variant, testId }) {
    const isDark = variant === "dark";
    // Deliberately using <div> (not <label>) + a single role=checkbox target so
    // pointer clicks never get forwarded to a hidden native input (which would
    // cause a double-toggle and make the box appear dead). Clicks on the row
    // wrapper OR the box OR the text all funnel into a single toggle handler.
    const toggle = () => onChange(!checked);
    return (
        <div
            className="flex cursor-pointer items-start gap-3 text-[12.5px] leading-relaxed"
            data-testid={`${testId}-row`}
            onClick={toggle}
        >
            <span
                role="checkbox"
                aria-checked={checked}
                tabIndex={0}
                onClick={(e) => {
                    // Prevent the wrapper's onClick from also firing.
                    e.stopPropagation();
                    toggle();
                }}
                onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        toggle();
                    }
                }}
                data-testid={testId}
                className={`mt-[2px] inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] transition ${
                    checked
                        ? "bg-gold ring-1 ring-gold"
                        : isDark
                            ? "bg-transparent ring-1 ring-ivory/40 hover:ring-ivory/70"
                            : "bg-white ring-1 ring-charcoal/25 hover:ring-charcoal/50"
                }`}
            >
                {checked && <Check className="h-[13px] w-[13px] text-charcoal" strokeWidth={3} />}
            </span>
            <span className={isDark ? "text-ivory/80" : "text-charcoal/85"}>{label}</span>
        </div>
    );
}
